import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Tickets, type TicketSummary } from '../lib/api';
import { useTicketEvents } from '../lib/events';
import { PriorityBadge } from '../components/Badge';

export function PriorityQueuePage() {
  // Two lists: ranked (the queue, drag-orderable) and unranked (candidates to add).
  const [ranked, setRanked] = useState<TicketSummary[]>([]);
  const [unranked, setUnranked] = useState<TicketSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const dragIndexRef = useRef<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      // Pull active tickets, large page. The API returns them sorted by rank composite.
      const res = await Tickets.list({ view: 'active', sort: 'rank', order: 'asc', limit: 100 });
      const r: TicketSummary[] = [];
      const u: TicketSummary[] = [];
      for (const t of res.tickets) (t.rank != null ? r : u).push(t);
      r.sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0));
      setRanked(r);
      setUnranked(u);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  // Refresh when something changes server-side, but don't yank away an in-flight drag.
  useTicketEvents(() => {
    if (dragIndexRef.current === null) void load();
  });

  async function persist(newRanked: TicketSummary[]) {
    setSaving(true);
    setError(null);
    try {
      await Tickets.reorder(newRanked.map((t) => t.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
      // Reload to recover from any partial server state.
      await load();
      return;
    } finally {
      setSaving(false);
    }
  }

  function applyReorder(from: number, to: number) {
    if (from === to) return;
    setRanked((prev) => {
      const next = [...prev];
      const moved = next.splice(from, 1)[0];
      if (!moved) return prev;
      next.splice(to, 0, moved);
      void persist(next);
      return next;
    });
  }

  async function addToQueue(t: TicketSummary) {
    const next = [...ranked, t];
    setRanked(next);
    setUnranked((u) => u.filter((x) => x.id !== t.id));
    await persist(next);
  }

  async function removeFromQueue(t: TicketSummary) {
    const next = ranked.filter((x) => x.id !== t.id);
    setRanked(next);
    setUnranked((u) => [t, ...u]);
    await persist(next);
  }

  const filteredUnranked = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return unranked;
    return unranked.filter(
      (t) =>
        t.title.toLowerCase().includes(s) ||
        String(t.id).includes(s) ||
        (t.assignee?.name.toLowerCase().includes(s) ?? false),
    );
  }, [unranked, search]);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold text-slate-900">Priority queue</h1>
        <p className="text-sm text-slate-500">
          Drag tickets up or down to set the team's priority order. Rank #1 is highest. The number
          you see here is the same one displayed in the Rank column on the tickets list.
        </p>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : (
        <>
          <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-700">In the queue</h2>
              <span className="text-xs text-slate-500">
                {ranked.length} ticket{ranked.length === 1 ? '' : 's'}
                {saving && ' · saving…'}
              </span>
            </div>
            {ranked.length === 0 ? (
              <p className="px-3 py-6 text-sm text-slate-500 text-center">
                Queue is empty. Add tickets from the list below.
              </p>
            ) : (
              <ul>
                {ranked.map((t, i) => (
                  <li
                    key={t.id}
                    draggable
                    onDragStart={() => { dragIndexRef.current = i; }}
                    onDragEnd={() => { dragIndexRef.current = null; setDragOverIndex(null); }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      if (dragIndexRef.current !== null && dragIndexRef.current !== i) {
                        setDragOverIndex(i);
                      }
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const from = dragIndexRef.current;
                      if (from !== null) applyReorder(from, i);
                      setDragOverIndex(null);
                    }}
                    className={`border-t border-slate-100 first:border-t-0 px-3 py-2 flex items-center gap-3 hover:bg-slate-50 cursor-move ${
                      dragOverIndex === i ? 'bg-violet-50' : ''
                    }`}
                  >
                    <span className="text-slate-500 select-none">⋮⋮</span>
                    <span className="text-slate-700 font-semibold tabular-nums w-6 text-right">
                      {i + 1}
                    </span>
                    <span className="text-slate-500 text-xs tabular-nums">#{t.id}</span>
                    <PriorityBadge priority={t.priority} />
                    <Link
                      to={`/tickets/${t.id}`}
                      className="flex-1 text-sm text-slate-900 hover:text-brand-600 truncate"
                    >
                      {t.title}
                    </Link>
                    <span className="text-xs text-slate-500 w-28 truncate">
                      {t.assignee?.name ?? <span className="italic">unassigned</span>}
                    </span>
                    <button
                      onClick={() => void removeFromQueue(t)}
                      className="text-xs text-slate-500 hover:text-red-600"
                      title="Remove from queue"
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between gap-3">
              <h2 className="text-sm font-semibold text-slate-700">Add to queue</h2>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter unranked tickets…"
                className="px-2 py-1 border border-slate-300 rounded text-sm w-56"
              />
            </div>
            {filteredUnranked.length === 0 ? (
              <p className="px-3 py-6 text-sm text-slate-500 text-center">
                {search ? 'No unranked tickets match that filter.' : 'No unranked active tickets.'}
              </p>
            ) : (
              <ul>
                {filteredUnranked.map((t) => (
                  <li
                    key={t.id}
                    className="border-t border-slate-100 first:border-t-0 px-3 py-2 flex items-center gap-3 hover:bg-slate-50"
                  >
                    <span className="text-slate-500 text-xs tabular-nums w-12">#{t.id}</span>
                    <PriorityBadge priority={t.priority} />
                    <Link
                      to={`/tickets/${t.id}`}
                      className="flex-1 text-sm text-slate-900 hover:text-brand-600 truncate"
                    >
                      {t.title}
                    </Link>
                    <span className="text-xs text-slate-500 w-28 truncate">
                      {t.assignee?.name ?? <span className="italic">unassigned</span>}
                    </span>
                    <button
                      onClick={() => void addToQueue(t)}
                      className="text-xs text-brand-600 hover:text-brand-700 font-medium"
                    >
                      + Add
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}
